import { useCallback, useEffect, useMemo, useState } from 'react';
import { BUILTIN_EXCEL_SHADES } from '../data/builtInExcelShades';
import { mergeShades, parseShadeWorkbook, type Shade } from '../lib/shade-library';

const STORAGE_KEY = 'user_excel_shades';

/** Shades from `additions` that are new to both the built-in library and `existing`, appended to `existing`. */
const addCustomShades = (existing: Shade[], additions: Shade[]) =>
  mergeShades(BUILTIN_EXCEL_SHADES, [...existing, ...additions]).slice(BUILTIN_EXCEL_SHADES.length);

function loadCustomShades(): Shade[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    // Older versions saved the whole library; this keeps only the user's own additions.
    return Array.isArray(saved) ? addCustomShades([], saved as Shade[]) : [];
  } catch {
    return [];
  }
}

function saveCustomShades(shades: Shade[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shades));
  } catch (err) {
    console.warn('Could not save imported shades', err);
  }
}

export type ImportStatus = { tone: 'info' | 'warning'; message: string } | null;

/**
 * The built-in Nippon/Asian Paints shade library plus shades imported from the
 * user's spreadsheets. Imports accumulate and persist in this browser.
 */
export function useShadeLibrary() {
  const [custom, setCustom] = useState<Shade[]>(loadCustomShades);
  const [status, setStatus] = useState<ImportStatus>(null);
  const shades = useMemo(() => [...BUILTIN_EXCEL_SHADES, ...custom], [custom]);

  useEffect(() => saveCustomShades(custom), [custom]);

  const importFile = useCallback(async (file: File) => {
    setStatus({ tone: 'info', message: 'Parsing Excel palette file...' });
    try {
      const parsed = await parseShadeWorkbook(await file.arrayBuffer());
      if (parsed.length === 0) {
        setStatus({
          tone: 'warning',
          message: 'No HEX codes found in Excel file. Please ensure columns contain HEX values (e.g. #F5F2EB).',
        });
        return;
      }
      setCustom((previous) => addCustomShades(previous, parsed));
      setStatus({
        tone: 'info',
        message: `Successfully loaded ${parsed.length} paint shades with Shade Names & Codes from ${file.name}! Saved to your color library.`,
      });
    } catch (err) {
      setStatus({
        tone: 'warning',
        message: `Error parsing file: ${err instanceof Error ? err.message : 'Invalid format'}`,
      });
    }
  }, []);

  return { shades, importFile, status };
}
